import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  VStack,
  Text,
  Heading,
  Image,
  Table,
  TableContainer,
  Tbody,
  Td,
  Thead,
  Tr,
  InputGroup,
  InputLeftElement,
  Input,
  useDisclosure,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Checkbox,
  LinkBox,
  LinkOverlay,
} from '@chakra-ui/react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDebounce } from 'usehooks-ts';

import { ReactComponent as DotsIcon } from '../../../assets/icons/dots.svg';
import { ReactComponent as FiltersIcon } from '../../../assets/icons/filters.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import TrashIcon from '../../../assets/icons/red-trash.svg';
import EditIcon from '../../../assets/icons/edit.svg';
import FolderIcon from '../../../assets/projects/folder.svg';
import MoveIcon from '../../../assets/projects/move.svg';
import BackIcon from '../../../assets/projects/back.svg';
import ArchiveIcon from '../../../assets/projects/archive.svg';
import {
  useGetDirectoryByPhaseIdQuery,
  useGetDirectoryByIdQuery,
  useDeleteDriverMutation,
  useArchiveDriverMutation,
  useMoveDriverMutation,
} from '../../../services';
import {
  FilterDrawer,
  CenteredSpinner,
  RoundedBadge,
  Paginator,
  TableHeader,
  UserData,
  DeleteModal,
} from '../../../components';
import { documentsFilters, isDirectory } from '../utils';
import {
  DriverData,
  Directory,
  DirectoryType,
  Document,
  DriverType,
  ProjectType,
  CompanyStatus,
} from '../../../models';

import { formatDate, matchIcon } from '../../../utils';
import { MoveDocument } from '.';
import {
  isMutationSuccess,
  notifyMutationFailure,
} from '../../../utils/mutation-feedback';

export const DocumentsList: FC<{ phaseId: string }> = ({ phaseId }) => {
  const [selectedDocument, setSelectedDocument] = useState<{
    id: number;
    isDirectory: boolean;
  }>();
  const [searchTerm, setSearchTerm] = useState<string>();
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<{ status?: CompanyStatus }>({
    status: undefined,
  });
  const [checkedDocuments, setCheckedDocuments] = useState<
    { id: number; isDirectory: boolean }[]
  >([]);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const {
    isOpen: isDeleteDocumentsModalOpen,
    onOpen: onOpenDeleteDocumentsModal,
    onClose: onCloseDeleteDocumentsModal,
  } = useDisclosure();

  const {
    isOpen: isMoveDocumentsModalOpen,
    onOpen: onOpenMoveDocumentsModal,
    onClose: onCloseMoveDocumentsModal,
  } = useDisclosure();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { projectId, directoryId } = useParams();
  const navigate = useNavigate();

  const { data: phaseDirectory, isFetching: isPhaseDirectoryLoading } =
    useGetDirectoryByPhaseIdQuery(
      {
        projectId: Number(projectId),
        phaseId: Number(phaseId),
        page,
      },
      { skip: Boolean(directoryId) }
    );

  const {
    data: nestedDirectory,
    isFetching: isNestedDirectoryLoading,
    refetch,
  } = useGetDirectoryByIdQuery(Number(directoryId), {
    skip: !directoryId,
  });

  let data: (Directory | Document)[] = [];
  if (directoryId && nestedDirectory?.data.subDirectories) {
    data = [
      ...nestedDirectory.data.subDirectories,
      ...nestedDirectory.data.documents,
    ];
  }
  if (!directoryId && phaseDirectory?.data.content.length) {
    data = [
      ...phaseDirectory.data.content[0].subDirectories,
      ...phaseDirectory.data.content[0].documents,
    ];
  }

  useEffect(() => {
    if (directoryId) void refetch();
  }, [directoryId]);

  const handleFilterDrawerClose = (filterValues?: {
    status: CompanyStatus;
  }) => {
    onClose();
    if (!filterValues) return;

    setFilters({ status: filterValues.status });

    void refetch();
  };

  const handleRemoveFilter = (filterKey: keyof typeof filters) => {
    setFilters({ ...filters, [filterKey]: undefined });

    void refetch();
  };

  const [archiveDriver] = useArchiveDriverMutation();
  const [moveDriver] = useMoveDriverMutation();
  const [deleteDriver] = useDeleteDriverMutation();

  const handelToggleCheckedDocuments = (id: number, isDirectory: boolean) => {
    if (checkedDocuments.find(doc => doc.id === id))
      setCheckedDocuments(
        checkedDocuments.filter(
          docs => docs.id !== id && docs.isDirectory === isDirectory
        )
      );
    else setCheckedDocuments([...checkedDocuments, { id, isDirectory }]);
  };

  const handleDriverData = (): DriverData[] => {
    let uniqueCheckedDocuments: { id: number; isDirectory: boolean }[] = [];
    if (checkedDocuments.length) uniqueCheckedDocuments = checkedDocuments;
    else if (!selectedDocument) return [];
    else uniqueCheckedDocuments = [selectedDocument];

    return uniqueCheckedDocuments.map(({ id, isDirectory }) => {
      if (isDirectory)
        return { driverType: DriverType.DIRECTORY, directoryId: String(id) };
      else return { driverType: DriverType.DOCUMENT, documentId: String(id) };
    });
  };

  // The three handlers below now report whether the mutation actually
  // succeeded, so the confirm dialog can stay open on a failure instead of
  // dismissing itself as if the documents had been archived/moved/deleted.
  const handleArchiveDocuments = async (): Promise<boolean> => {
    const archivedDocuments = handleDriverData();
    const response = await archiveDriver(archivedDocuments);

    if (!isMutationSuccess(response)) {
      notifyMutationFailure(response, {
        title: 'Could not archive the selected documents',
        description: 'Nothing was archived. Please try again.',
      });
      return false;
    }

    return true;
  };

  const handleMoveDocuments = async (id: number): Promise<boolean> => {
    const movedDocuments = handleDriverData().map(data => {
      if (data.driverType === DriverType.DIRECTORY) {
        data.sourceDirectoryId = data.directoryId;
        delete data.directoryId;
      }

      data.destinationDirectoryId = id;

      return data;
    });
    const response = await moveDriver(movedDocuments);

    if (!isMutationSuccess(response)) {
      notifyMutationFailure(response, {
        title: 'Could not move the selected documents',
        description:
          'Nothing was moved — the documents are still in their original folder.',
      });
      return false;
    }

    return true;
  };

  const handleDeleteDocuments = async (): Promise<boolean> => {
    const deletedDocuments = handleDriverData();
    const response = await deleteDriver(deletedDocuments);

    if (!isMutationSuccess(response)) {
      notifyMutationFailure(response, {
        title: 'Could not delete the selected documents',
        description: 'Nothing was deleted. Please try again.',
      });
      return false;
    }

    return true;
  };

  return (
    <Box h="full">
      {isOpen && (
        <FilterDrawer
          isOpen={isOpen}
          onClose={handleFilterDrawerClose}
          filters={documentsFilters}
        />
      )}

      {isDeleteDocumentsModalOpen && (
        <DeleteModal
          isOpen={isDeleteDocumentsModalOpen}
          onClose={async isDeleted => {
            if (!isDeleted) return onCloseDeleteDocumentsModal();

            const isDeletionConfirmed = await handleDeleteDocuments();
            if (isDeletionConfirmed) onCloseDeleteDocumentsModal();
          }}
        />
      )}

      {isMoveDocumentsModalOpen && (
        <MoveDocument
          phaseId={phaseId}
          isOpen={isMoveDocumentsModalOpen}
          onClose={async id => {
            if (!id) return onCloseMoveDocumentsModal();

            const isMoveConfirmed = await handleMoveDocuments(id);
            if (isMoveConfirmed) onCloseMoveDocumentsModal();
          }}
        />
      )}

      <Box display="flex" flexDirection="column" flex="1" h="full">
        <HStack justify="space-between" mt="4" align="start">
          <HStack>
            {directoryId &&
              nestedDirectory?.data.parentDirectory?.directoryTypeEnum !==
                DirectoryType.PROJECT && (
                <Image
                  src={BackIcon}
                  alt="Back to previous directory"
                  cursor="pointer"
                  onClick={() => {
                    let url = `/projects`;

                    if (
                      nestedDirectory?.data.project.projectTypeEnum ===
                      ProjectType.SINGLE
                    ) {
                      url += `/single-phase/${String(
                        projectId
                      )}/project-details/documents`;
                    }

                    if (
                      nestedDirectory?.data.project.projectTypeEnum ===
                      ProjectType.MULTI
                    ) {
                      url += `/multiple-phase/${String(
                        projectId
                      )}/project-details/phases/${String(phaseId)}/documents`;
                    }

                    url += `/${Number(
                      nestedDirectory?.data.parentDirectory?.id
                    )}`;

                    navigate(url);
                  }}
                />
              )}

            <Heading color="custom_blue.1" fontSize="2xl">
              {!directoryId ||
              nestedDirectory?.data.parentDirectory?.directoryTypeEnum ===
                DirectoryType.PROJECT
                ? 'Documents'
                : nestedDirectory?.data.directoryName}
            </Heading>
          </HStack>

          <HStack>
            {/* TODO: reuse after implementation */}
            {/* <Button
              bg="transparent"
              _hover={{ bg: 'transparent' }}
              color="custom_blue.1"
              fontWeight="semibold"
              fontSize="sm"
              variant="outline"
              borderColor="custom_gray.1"
            >
              Archived Documents
            </Button> */}

            <Menu>
              <MenuButton
                as={Button}
                bg="custom_orange.2"
                _hover={{ bg: 'custom_orange.2' }}
                _active={{ bg: 'custom_orange.2' }}
                color="white"
                fontWeight="semibold"
                fontSize="sm"
                leftIcon={<PlusIcon />}
              >
                Add
              </MenuButton>
              <MenuList p="0">
                <MenuItem
                  py="3"
                  border="1px"
                  borderColor="blackAlpha.50"
                  _hover={{
                    bg: 'custom_orange.4',
                    color: 'custom_orange.2',
                  }}
                  onClick={() => {
                    const url = directoryId
                      ? `new-directory`
                      : `${String(
                          phaseDirectory?.data.content[0].id
                        )}/new-directory`;
                    navigate(url);
                  }}
                >
                  <HStack>
                    <Image src={ArchiveIcon} alt="New Folder" />
                    <Text fontSize="sm" fontWeight="medium">
                      New Folder
                    </Text>
                  </HStack>
                </MenuItem>

                <MenuItem
                  py="3"
                  border="1px"
                  borderColor="blackAlpha.50"
                  _hover={{
                    bg: 'custom_orange.4',
                    color: 'custom_orange.2',
                  }}
                  onClick={() => {
                    const url = directoryId
                      ? `new-document`
                      : `${String(
                          phaseDirectory?.data.content[0].id
                        )}/new-document`;
                    navigate(url);
                  }}
                >
                  <HStack>
                    <Image src={EditIcon} alt="File Upload" />
                    <Text fontSize="sm" fontWeight="medium">
                      File Upload
                    </Text>
                  </HStack>
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        </HStack>

        <VStack align="stretch" bg="white" my="4" gap="3">
          <HStack py="2">
            <InputGroup flexBasis="45%">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search"
                onChange={e => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            <Button
              leftIcon={<FiltersIcon />}
              color="custom_blue.1"
              fontWeight="semibold"
              fontSize="sm"
              boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
              border="1px"
              borderColor="custom_blue.3"
              bg="none"
              _hover={{ bg: 'none' }}
              onClick={onOpen}
            >
              Filters
            </Button>
          </HStack>

          {filters.status && (
            <HStack>
              <RoundedBadge
                colorSchema={filters.status}
                withCloseButton
                onClose={handleRemoveFilter}
              >
                {filters.status}
              </RoundedBadge>
            </HStack>
          )}

          {isPhaseDirectoryLoading || isNestedDirectoryLoading ? (
            <CenteredSpinner />
          ) : (
            <Box>
              <TableContainer
                h="full"
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                minH="70vh"
              >
                <Table variant="simple">
                  <Thead bg="custom_gray.2">
                    <Tr>
                      <TableHeader title="Document Name" />
                      <TableHeader title="Created At" />
                      <TableHeader title="Created By" />
                      <TableHeader title="Updated At" />
                      <TableHeader title="Actions" />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.map(item => (
                      <LinkBox key={item.id} as={Tr}>
                        <Td border="none">
                          <HStack>
                            <Checkbox
                              zIndex={1}
                              colorScheme="orange"
                              onChange={() =>
                                handelToggleCheckedDocuments(
                                  item.id,
                                  isDirectory(item)
                                )
                              }
                            />
                            <HStack w="10" h="10">
                              <Image
                                // TODO: get the default file image from UI/UX
                                fallbackSrc="/icons/txt.svg"
                                src={
                                  isDirectory(item)
                                    ? FolderIcon
                                    : matchIcon(item.documentOriginalFileName)
                                }
                              />
                            </HStack>
                            <VStack align="flex-start">
                              {isDirectory(item) ? (
                                <Box>
                                  <Text
                                    color="custom_blue.1"
                                    fontWeight="semibold"
                                    fontSize="sm"
                                  >
                                    <LinkOverlay
                                      as={Link}
                                      to={
                                        item.project.projectTypeEnum ===
                                        ProjectType.SINGLE
                                          ? `/projects/single-phase/${String(
                                              projectId
                                            )}/project-details/documents/${
                                              item.id
                                            }`
                                          : `/projects/multiple-phase/${String(
                                              projectId
                                            )}/project-details/phases/${phaseId}/documents/${
                                              item.id
                                            }`
                                      }
                                    >
                                      {item.directoryName}
                                    </LinkOverlay>
                                  </Text>

                                  <Text color="custom_blue.1" fontSize="xs">
                                    {item.directoryTags.map(({ id, name }) => (
                                      <Box key={id}>{name} </Box>
                                    ))}
                                  </Text>
                                </Box>
                              ) : (
                                <LinkOverlay
                                  as={Link}
                                  to={item.downloadURL}
                                  target="_blank"
                                >
                                  <Text
                                    color="custom_blue.1"
                                    fontWeight="semibold"
                                    fontSize="sm"
                                  >
                                    {item.documentOriginalFileName}
                                  </Text>
                                </LinkOverlay>
                              )}
                            </VStack>
                          </HStack>
                        </Td>
                        <Td border="none">{formatDate(item.createdAt)}</Td>
                        <Td border="none">
                          <UserData
                            roleName={
                              isDirectory(item)
                                ? item.directoryOwnerRoleName
                                : item.documentOwnerRoleName
                            }
                            firstName={
                              isDirectory(item)
                                ? item.directoryOwnerName
                                : item.documentOwnerName
                            }
                            lastName={''}
                            pictureUrl={
                              isDirectory(item)
                                ? item.directoryOwnerImage
                                : item.documentOwnerImage
                            }
                          />
                        </Td>
                        <Td border="none">{formatDate(item.updatedAt)}</Td>

                        <Td color="custom_blue.1" fontSize="xs" border="none">
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              icon={<DotsIcon />}
                              aria-label="Actions of documents"
                              bg="transparent"
                            />
                            <MenuList p="0">
                              {/* TODO: reuse after implementation */}
                              {/* <MenuItem
                              py="3"
                              border="1px"
                              borderColor="blackAlpha.50"
                              onClick={handleArchiveDocuments}
                              _hover={{
                                bg: 'custom_orange.4',
                                color: 'custom_orange.2',
                              }}
                            >
                              <HStack>
                                <Image
                                  src={ArchiveIcon}
                                  alt="Archive document"
                                />
                                <Text fontSize="sm" fontWeight="medium">
                                  Archive
                                </Text>
                              </HStack>
                            </MenuItem> */}

                              {isDirectory(item) && (
                                <MenuItem
                                  py="3"
                                  border="1px"
                                  borderColor="blackAlpha.50"
                                  _hover={{
                                    bg: 'custom_orange.4',
                                    color: 'custom_orange.2',
                                  }}
                                  onClick={() => {
                                    let url = `/projects/${
                                      item.project.projectTypeEnum ===
                                      ProjectType.SINGLE
                                        ? 'single-phase'
                                        : 'multiple-phase'
                                    }/${String(projectId)}/project-details/`;

                                    url +=
                                      item.project.projectTypeEnum ===
                                      ProjectType.SINGLE
                                        ? `documents/${item.id}/update-directory`
                                        : `phases/${phaseId}/documents/${item.id}/update-directory`;
                                    navigate(url);
                                  }}
                                >
                                  <HStack>
                                    <Image src={EditIcon} alt="Edit document" />
                                    <Text fontSize="sm" fontWeight="medium">
                                      Edit
                                    </Text>
                                  </HStack>
                                </MenuItem>
                              )}

                              <MenuItem
                                py="3"
                                border="1px"
                                borderColor="blackAlpha.50"
                                _hover={{
                                  bg: 'custom_orange.4',
                                  color: 'custom_orange.2',
                                }}
                                onClick={() => {
                                  setSelectedDocument({
                                    id: item.id,
                                    isDirectory: isDirectory(item),
                                  });
                                  onOpenMoveDocumentsModal();
                                }}
                              >
                                <HStack>
                                  <Image src={MoveIcon} alt="Move document" />
                                  <Text fontSize="sm" fontWeight="medium">
                                    Move
                                  </Text>
                                </HStack>
                              </MenuItem>

                              <MenuItem
                                py="3"
                                border="1px"
                                borderColor="blackAlpha.50"
                                _hover={{
                                  bg: 'custom_orange.4',
                                  color: 'custom_orange.2',
                                }}
                                onClick={() => {
                                  setSelectedDocument({
                                    id: item.id,
                                    isDirectory: isDirectory(item),
                                  });
                                  onOpenDeleteDocumentsModal();
                                }}
                              >
                                <HStack>
                                  <Image src={TrashIcon} alt="Delete phase" />
                                  <Text fontSize="sm" fontWeight="medium">
                                    Delete
                                  </Text>
                                </HStack>
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        </Td>
                      </LinkBox>
                    ))}
                  </Tbody>
                </Table>

                {/* 
              <Paginator
                pageCount={Number(data?.data.totalElements)}
                onPageChange={page => setPage(page)}
              /> */}
              </TableContainer>
            </Box>
          )}
        </VStack>
      </Box>
    </Box>
  );
};
